<script setup lang="ts">
import { Columns2, Columns3, Download, FolderInput, GalleryVerticalEnd, Grid2X2, Grid3X3, LayoutList, PanelsTopLeft, Trash2, Upload } from 'lucide-vue-next';
import GalleryImageGrid from '~/components/files/GalleryImageGrid.vue';
import FileViewLayoutControl from '~/components/files/FileViewLayoutControl.vue';
import ScrollableFileList from '~/components/files/ScrollableFileList.vue';
import ConfirmDialog from '@/components/editing/ConfirmDialog.vue';
import UploadedImageDetailsSheet from '@/components/files/UploadedImageDetailsSheet.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import FileSelectDropzone from '@/components/ui/file-upload/FileSelectDropzone.vue';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import SelectionActionBar from '@/components/selection/SelectionActionBar.vue';
import SelectionToggle from '@/components/selection/SelectionToggle.vue';
import type { SelectionAction, SelectionActionEvent } from '@/components/selection/types';
import { useUploadedFileActions } from '~/composables/files/useUploadedFileActions';
import { useUploadedFileSelection } from '~/composables/files/useUploadedFileSelection';
import type { UploadedFile } from '~/types/models';
import type { ClientDeliveryUploadScopeOption } from './types';

interface Props {
    uploadScope: string;
    uploadScopeOptions: ClientDeliveryUploadScopeOption[];
    selectedFilesCount: number;
    deliveryUploading: boolean;
    deliveryUploadError: string;
    galleryFilesAcrossProjects: UploadedFile[];
    filesLoading: boolean;
    scopedFiles: UploadedFile[];
    canMutate: boolean;
    projectOptions: Array<{ value: string, label: string }>;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (e: 'update:uploadScope', value: string): void,
    (e: 'files-selected', files: File[]): void,
    (e: 'upload-files'): void,
    (e: 'refresh-files'): void,
}>();

const fileLayout = ref<'list' | 'grid'>('list');
const galleryLayout = ref<'one' | 'two' | 'three' | 'four' | 'masonry'>('two');
const fileLayoutOptions = [
    { value: 'list', label: 'List view', icon: LayoutList },
    { value: 'grid', label: 'Grid view', icon: Grid2X2 },
];
const galleryLayoutOptions = [
    { value: 'one', label: 'One column', icon: GalleryVerticalEnd },
    { value: 'two', label: 'Two columns', icon: Columns2 },
    { value: 'three', label: 'Three columns', icon: Columns3 },
    { value: 'four', label: 'Four columns', icon: Grid3X3 },
    { value: 'masonry', label: 'Masonry view', icon: PanelsTopLeft },
];

const selectableFiles = computed(() => {
    const filesById = new Map<string, UploadedFile>();
    [...props.scopedFiles, ...props.galleryFilesAcrossProjects].forEach((file) => filesById.set(file.id, file));
    return [...filesById.values()];
});
const fileIds = computed(() => props.scopedFiles.map((file) => file.id));
const galleryFileIds = computed(() => props.galleryFilesAcrossProjects.map((file) => file.id));

const {
    selectedFileIds,
    selectedFiles,
    toggleFileSelection,
    toggleAllFileSelection,
    selectionStateFor,
    clearFileSelection,
} = useUploadedFileSelection(selectableFiles);

const {
    busyFileId,
    actionError,
    deleteCandidate,
    deleteOpen,
    editCandidate,
    editOpen,
    bulkDeleteCandidates,
    bulkDeleteOpen,
    bulkLoading,
    bulkLoadingLabel,
    requestDelete,
    requestEdit,
    requestBulkDelete,
    confirmDelete,
    confirmBulkDelete,
    saveImageDetails,
    setCover,
    togglePortalVisibility,
    addToProject,
    assignFilesToProject,
    downloadFilesAsZip,
} = useUploadedFileActions({
    canMutate: () => props.canMutate,
    onChanged: () => emit('refresh-files'),
});

const selectionActions = computed<SelectionAction[]>(() => [
    {
        id: 'assign-project',
        label: 'Assign to project',
        icon: FolderInput,
        menuLabel: 'Choose project',
        disabled: selectedFiles.value.some((file) => file.entityType !== 'client'),
        options: props.projectOptions.map((project) => ({ id: project.value, label: project.label, value: project.value })),
    },
    { id: 'download-zip', label: 'Download ZIP', icon: Download },
    {
        id: 'delete',
        label: 'Delete',
        icon: Trash2,
        tone: 'destructive',
        separatorBefore: true,
        disabled: !props.canMutate,
    },
]);

async function handleSelectionAction(payload: SelectionActionEvent) {
    if (payload.actionId === 'assign-project' && payload.option) {
        if (await assignFilesToProject(selectedFiles.value, String(payload.option.value || payload.option.id))) clearFileSelection();
        return;
    }
    if (payload.actionId === 'download-zip') {
        if (await downloadFilesAsZip(selectedFiles.value, 'client-uploads.zip')) clearFileSelection();
        return;
    }
    if (payload.actionId === 'delete') requestBulkDelete(selectedFiles.value);
}

async function confirmSelectedDelete() {
    if (await confirmBulkDelete()) clearFileSelection();
}

</script>

<template>
    <SectionSurface
        eyebrow="Assets"
        title="Files and gallery"
        description="Upload shared files, browse every asset, and review imagery across this client workspace."
        :icon="Upload"
        density="compact"
        presentation="editorial"
    >
        <div class="space-y-8">
            <section class="space-y-4" aria-labelledby="client-upload-heading">
                <div>
                    <p id="client-upload-heading" class="text-sm font-semibold text-foreground">Upload files</p>
                    <p class="mt-1 text-sm text-muted-foreground">Choose the client workspace or a linked project, then drop files into the upload area below.</p>
                </div>

                <div class="space-y-2 sm:max-w-xl">
                    <Label for="delivery-upload-target">Upload target</Label>
                    <SearchableSelectMenu
                        id="delivery-upload-target"
                        :model-value="uploadScope"
                        :options="uploadScopeOptions"
                        placeholder="Choose where files should live"
                        search-placeholder="Search upload targets..."
                        show-all-by-default
                        trigger-class="flex h-11 w-full items-center justify-between rounded-xl border border-input bg-background/85 px-3 text-left text-sm"
                        @update:model-value="emit('update:uploadScope', String($event || ''))"
                    >
                        <template #trigger="{ option }">
                            <div class="flex w-full items-center justify-between gap-3">
                                <span class="truncate text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                                    {{ option?.label || 'Choose where files should live' }}
                                </span>
                                <span class="text-xs text-muted-foreground">Change</span>
                            </div>
                        </template>
                    </SearchableSelectMenu>
                </div>

                <FileSelectDropzone
                    title="Drag and drop files here"
                    description="Browse and upload multiple files in one go. Project images join their gallery automatically."
                    button-label="Browse files"
                    :disabled="deliveryUploading || !uploadScope"
                    @files-selected="emit('files-selected', $event)"
                >
                    <div v-if="selectedFilesCount || deliveryUploadError" class="flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-3">
                        <p class="text-xs" :class="deliveryUploadError ? 'text-destructive' : 'text-muted-foreground'">
                            {{ deliveryUploadError || `${selectedFilesCount} file${selectedFilesCount === 1 ? '' : 's'} ready to upload` }}
                        </p>
                        <Button type="button" size="sm" :disabled="!selectedFilesCount || deliveryUploading" @click="emit('upload-files')">
                            <Upload class="mr-2 size-4" />{{ deliveryUploading ? 'Uploading…' : 'Upload files' }}
                        </Button>
                    </div>
                </FileSelectDropzone>
            </section>

            <section class="border-t border-border/60 pt-6" aria-labelledby="client-files-heading">
                <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p id="client-files-heading" class="text-sm font-semibold text-foreground">Files</p>
                        <p class="mt-1 text-sm text-muted-foreground">Every uploaded asset remains available without making the page grow indefinitely.</p>
                    </div>
                    <div class="flex flex-wrap items-center justify-end gap-2">
                        <SelectionToggle
                            :model-value="selectionStateFor(fileIds)"
                            label="Select all files"
                            :disabled="filesLoading || fileIds.length === 0"
                            @toggle="toggleAllFileSelection(fileIds)"
                        />
                        <Badge variant="outline">
                            {{ filesLoading ? 'Loading…' : `${scopedFiles.length} ${scopedFiles.length === 1 ? 'file' : 'files'}` }}
                        </Badge>
                        <FileViewLayoutControl v-model="fileLayout" label="File layout" :options="fileLayoutOptions" />
                    </div>
                </div>
                <ScrollableFileList
                    :files="scopedFiles"
                    :loading="filesLoading"
                    empty-text="No files uploaded yet."
                    aria-label="Client files"
                    show-scope
                    show-actions
                    selectable
                    :layout="fileLayout"
                    :selected-file-ids="selectedFileIds"
                    :can-mutate="canMutate"
                    :project-options="projectOptions"
                    :busy-file-id="busyFileId"
                    @add-to-project="addToProject"
                    @set-cover="setCover"
                    @toggle-portal-visibility="togglePortalVisibility"
                    @delete="requestDelete"
                    @toggle-select="toggleFileSelection"
                />
            </section>

            <section class="border-t border-border/60 pt-6" aria-labelledby="client-gallery-heading">
                <div class="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p id="client-gallery-heading" class="text-sm font-semibold text-foreground">Gallery</p>
                        <p class="mt-1 text-sm text-muted-foreground">Images from the client workspace and linked projects load as you scroll.</p>
                    </div>
                    <div class="flex flex-wrap items-center justify-end gap-2">
                        <SelectionToggle
                            :model-value="selectionStateFor(galleryFileIds)"
                            label="Select all images"
                            :disabled="filesLoading || galleryFileIds.length === 0"
                            @toggle="toggleAllFileSelection(galleryFileIds)"
                        />
                        <Badge variant="outline">
                            {{ filesLoading ? 'Loading…' : `${galleryFilesAcrossProjects.length} ${galleryFilesAcrossProjects.length === 1 ? 'image' : 'images'}` }}
                        </Badge>
                        <FileViewLayoutControl v-model="galleryLayout" label="Gallery layout" :options="galleryLayoutOptions" />
                    </div>
                </div>
                <GalleryImageGrid
                    :files="galleryFilesAcrossProjects"
                    :loading="filesLoading"
                    empty-text="No gallery images yet. Upload a JPG or PNG and it will appear here."
                    variant="compact"
                    scrollable
                    show-controls
                    selectable
                    :show-reorder-controls="false"
                    :layout="galleryLayout"
                    :selected-file-ids="selectedFileIds"
                    :project-options="projectOptions"
                    :busy-file-id="busyFileId"
                    :allow-add-to-project="canMutate"
                    :allow-set-cover="canMutate"
                    :allow-toggle-portal-visibility="canMutate"
                    :allow-edit="canMutate"
                    :allow-delete="canMutate"
                    @add-to-project="addToProject"
                    @set-cover="setCover"
                    @toggle-portal-visibility="togglePortalVisibility"
                    @edit="requestEdit"
                    @remove="requestDelete"
                    @toggle-select="toggleFileSelection"
                />
            </section>

            <p v-if="actionError" class="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
                {{ actionError }}
            </p>
        </div>
    </SectionSurface>

    <ConfirmDialog
        v-model:open="deleteOpen"
        title="Delete this file?"
        :description="`${deleteCandidate?.fileName || 'This file'} will be permanently removed. This cannot be undone.`"
        confirm-label="Delete file"
        destructive
        :loading="Boolean(busyFileId)"
        @confirm="confirmDelete"
    />

    <UploadedImageDetailsSheet
        v-model:open="editOpen"
        :file="editCandidate"
        :saving="Boolean(busyFileId)"
        :error="actionError"
        @save="saveImageDetails"
    />

    <ConfirmDialog
        v-model:open="bulkDeleteOpen"
        :title="`Delete ${bulkDeleteCandidates.length} selected file${bulkDeleteCandidates.length === 1 ? '' : 's'}?`"
        :description="`${bulkDeleteCandidates.length} selected file${bulkDeleteCandidates.length === 1 ? '' : 's'} will be permanently removed. This cannot be undone.`"
        confirm-label="Yes, delete files"
        destructive
        :loading="bulkLoading"
        @confirm="confirmSelectedDelete"
    />

    <SelectionActionBar
        :selected-count="bulkDeleteOpen ? 0 : selectedFiles.length"
        :actions="selectionActions"
        :loading="bulkLoading"
        :loading-label="bulkLoadingLabel"
        @action="handleSelectionAction"
        @clear="clearFileSelection"
    />
</template>
